import { createAsyncThunk } from "@reduxjs/toolkit";

import * as productsAPI from "../../../api/products";
import {
    IBaseCompareTile,
    ICompareTile,
} from "../../../models/product-compare";
import { getChildFilterVariants } from "../selectors";

/**
 * Loads a single comparison tile with full product data.
 * Fetches product details for each product ID and applies child filters if specified.
 */
const loadSingleTile = async (
    baseTile: IBaseCompareTile,
): Promise<ICompareTile> => {
    const rootProducts = await Promise.all(
        baseTile.product_compare_tile.product_ids.map((pid) => {
            const productURL = productsAPI.getProductURL(pid);
            return productsAPI.getProduct(productURL);
        }),
    );
    const annotatedTile: ICompareTile = {
        ...baseTile,
        rootProducts: rootProducts,
    };
    if (baseTile.product_compare_tile.child_filter) {
        const childFilterVariants = getChildFilterVariants(
            annotatedTile,
            baseTile.product_compare_tile.child_filter,
        );
        return {
            ...annotatedTile,
            childFilterProducts: childFilterVariants,
        };
    }
    return annotatedTile;
};

/**
 * Loads multiple comparison tiles with full product data.
 *
 * This thunk:
 * 1. Takes an array of base tiles (containing product IDs and configuration)
 * 2. Fetches full product data for each tile's products in parallel
 * 3. Applies any child filters specified in the tile configuration
 * 4. Returns fully hydrated comparison tiles ready for rendering
 *
 * Used when initializing the product comparison grid from Wagtail page data.
 */
export const loadTiles = createAsyncThunk(
    "productcompare/loadTiles",
    async (
        baseTiles: ReadonlyArray<IBaseCompareTile>,
    ): Promise<ICompareTile[]> => {
        const loading = baseTiles.map((baseTile) => {
            return loadSingleTile(baseTile);
        });
        const tiles = await Promise.all(loading);
        return tiles;
    },
);
